import { openDB, type IDBPDatabase } from 'idb';
import { apiUrl } from './api-config';
import { boundedFetch } from './bounded-fetch';

const DB_NAME = 'compostdev_offline';
const DB_VERSION = 1;
const STORE_NAME = 'pending_actions';

export type QueuedActionType = 'temperature' | 'turn' | 'material';

export interface QueuedAction {
  id: string;
  scope: string;
  photoUrl?: string;
  type: QueuedActionType;
  windrowId: number;
  payload: Record<string, unknown>;
  photoBlob?: Blob;
  createdAt: string;
  status: 'pending' | 'syncing' | 'synced' | 'failed';
  retryCount: number;
  lastError?: string;
}

let activeScope: string | null = null;
let syncPromise: Promise<{ synced: number; failed: number }> | null = null;
export function setQueueScope(scope: string | null) { activeScope = scope; }
export function queueScope(user: { id: string; organization_id?: number | null } | null): string | null {
  return user?.id && user.organization_id ? `${user.organization_id}:${user.id}` : null;
}
function requireScope(): string {
  if (!activeScope) throw new Error('Sign in to a verified workspace before saving.');
  return activeScope;
}
let dbPromise: Promise<IDBPDatabase> | null = null;

function getDb() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('status', 'status');
          store.createIndex('createdAt', 'createdAt');
        }
      },
    });
  }
  return dbPromise;
}

async function withQueueLock<T>(work: () => Promise<T>): Promise<T> {
  if (typeof navigator !== 'undefined' && navigator.locks) {
    return navigator.locks.request('compostdev-offline-sync', work);
  }
  return work(); // API action IDs still prevent duplicate records on older WebViews.
}

/** Add an action to the offline queue. */
export async function enqueue(
  type: QueuedActionType,
  windrowId: number,
  payload: Record<string, unknown>,
  photoBlob?: Blob
): Promise<QueuedAction> {
  const scope = requireScope();
  const db = await getDb();
  const action: QueuedAction = {
    id: crypto.randomUUID(),
    scope,
    type,
    windrowId,
    payload,
    photoBlob,
    createdAt: new Date().toISOString(),
    status: 'pending',
    retryCount: 0,
  };
  await db.put(STORE_NAME, action);
  return action;
}

/** Get all pending (unsynced) actions in FIFO order. */
export async function getPendingActions(): Promise<QueuedAction[]> {
  const db = await getDb();
  const all = await db.getAllFromIndex(STORE_NAME, 'status', 'pending');
  return all.filter((action: QueuedAction) => !!activeScope && action.scope === activeScope).sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
}

/** Get count of pending actions. */
export async function getPendingCount(): Promise<number> {
  const db = await getDb();
  return (await getAllActions()).filter(action => action.status !== 'synced').length;
}

/** Get all actions (for Status screen). */
export async function getAllActions(): Promise<QueuedAction[]> {
  const db = await getDb();
  const all = await db.getAll(STORE_NAME);
  return all.filter((action: QueuedAction) => !!activeScope && action.scope === activeScope).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

/** Mark an action as synced and remove it. */
export async function markSynced(id: string): Promise<void> {
  const db = await getDb();
  const action = await db.get(STORE_NAME, id);
  if (action?.scope === activeScope) await db.delete(STORE_NAME, id);
}

/** Mark an action as failed. */
export async function markFailed(id: string, error: string): Promise<void> {
  const db = await getDb();
  const action = await db.get(STORE_NAME, id);
  if (action && action.scope === activeScope) {
    action.status = 'failed';
    action.retryCount += 1;
    action.lastError = error;
    await db.put(STORE_NAME, action);
  }
}

/** Reset a failed action to pending for retry. */
export async function retryAction(id: string): Promise<void> {
  const db = await getDb();
  const action = await db.get(STORE_NAME, id);
  if (action && action.scope === activeScope) {
    action.status = 'pending';
    await db.put(STORE_NAME, action);
  }
}

/** Clear all synced/completed actions. */
export async function clearCompleted(): Promise<void> {
  const db = await getDb();
  const all = await db.getAll(STORE_NAME);
  const tx = db.transaction(STORE_NAME, 'readwrite');
  for (const action of all) {
    if (action.scope === activeScope && action.status === 'synced') {
      await tx.store.delete(action.id);
    }
  }
  await tx.done;
}

/** Endpoint path for each action type. */
function getEndpoint(type: QueuedActionType, windrowId: number): string {
  switch (type) {
    case 'temperature':
      return `/api/windrows/${windrowId}/temperatures`;
    case 'turn':
      return `/api/windrows/${windrowId}/turns`;
    case 'material':
      return `/api/windrows/${windrowId}/materials`;
  }
}

/**
 * Process a single queued action — POST it to the API.
 * Returns true if successful, false if failed.
 */
export async function syncAction(
  action: QueuedAction,
  token: string
): Promise<boolean> {
  const db = await getDb();

  if (!activeScope || action.scope !== activeScope) return false;
  const scope = activeScope;
  // Mark as syncing
  action.status = 'syncing';
  await db.put(STORE_NAME, action);

  try {
    // If there's a photo, upload it first
    let photoUrl: string | null = action.photoUrl || null;
    if (action.photoBlob && !photoUrl) {
      const formData = new FormData();
      formData.append('photo', action.photoBlob, 'photo.jpg');
      formData.append('windrowId', String(action.windrowId));
      formData.append('type', action.type === 'temperature' ? 'temp' : action.type);

      const uploadRes = await boundedFetch(apiUrl('/api/upload/photo'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!uploadRes.ok) throw new Error(`Photo upload failed (${uploadRes.status}). Photo retained on this device.`);
      const uploadData = await uploadRes.json();
      if (!uploadData.url) throw new Error('Photo upload returned no URL. Photo retained on this device.');
      photoUrl = uploadData.url;
      action.photoUrl = photoUrl!;
      await db.put(STORE_NAME, action);
    }

    if (activeScope !== scope) throw new Error('Account changed. Sign back into the original account to retry.');
    // POST the action
    const endpoint = getEndpoint(action.type, action.windrowId);
    const body: Record<string, unknown> = { ...action.payload, clientActionId: action.id, recordedAt: action.payload.recordedAt || action.createdAt };
    if (photoUrl) {
      body.photoUrl = photoUrl;
    }

    const res = await boundedFetch(apiUrl(endpoint), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': action.id,
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (res.ok) {
      await db.delete(STORE_NAME, action.id);
      return true;
    } else {
      const data = await res.json().catch(() => ({}));
      action.status = 'failed'; action.retryCount += 1;
      action.lastError = data.error || `HTTP ${res.status}`;
      await db.put(STORE_NAME, action);
      return false;
    }
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Network error';
    action.status = 'failed'; action.retryCount += 1; action.lastError = message;
    await db.put(STORE_NAME, action);
    return false;
  }
}

/**
 * Process all pending actions in FIFO order.
 * Returns { synced: number, failed: number }.
 */
export async function recoverInterrupted(): Promise<void> {
  if (syncPromise) return;
  await withQueueLock(async () => {
    const db = await getDb();
    for (const action of await getAllActions()) {
      if (action.status === 'syncing') {
        action.status = 'failed';
        action.lastError = 'Upload interrupted. Retry safely using the original submission ID.';
        await db.put(STORE_NAME, action);
      }
    }
  });
}
export async function getUnownedCount(): Promise<number> {
  const db = await getDb();
  return (await db.getAll(STORE_NAME)).filter(action => !action.scope).length;
}
export async function syncAll(
  token: string,
  onProgress?: (synced: number, total: number) => void
): Promise<{ synced: number; failed: number }> {
  if (syncPromise) return syncPromise;
  const scope = requireScope();
  syncPromise = withQueueLock(async () => {
    const pending = await getPendingActions();
    let synced = 0; let failed = 0;
    for (let i = 0; i < pending.length; i++) {
      if (scope !== activeScope) break;
      const success = await syncAction(pending[i], token);
      if (success) synced++; else failed++;
      onProgress?.(i + 1, pending.length);
      if (!success) break; // Preserve FIFO and avoid retry storms.
    }
    return { synced, failed };
  });
  try { return await syncPromise; } finally { syncPromise = null; }
}
